<template>

    <div class="">
        <form action="">
            <div class="row">
                <div class="col-9">
                    <div class="row">
                        <div class="col-12 col-md-6">
                            <AdminBlock title="Purchase Order">

                                <!-- One line, tracking the field below it as it is
                                     typed. Chasing is not offered here: the PO Manager
                                     takes ready-to-invoice and signed-off jobs only, so
                                     a reminder sent from this step would be one the
                                     worklist never counted. The confirmation step
                                     offers whichever chase the job is actually due. -->
                                <p>
                                    <small v-if="poIsOutstanding" class="text-danger">
                                        This Customer requires a PO to process Invoices. Without one the job
                                        can still be signed off, and will be chased on the PO Manager.
                                    </small>
                                    <small v-else-if="job.customer.requires_po_for_invoice">
                                        This Customer requires a PO to process Invoices.
                                    </small>
                                    <small v-else>
                                        This Customer does not typically require a PO to process Invoices but it is recommended you add one where possible.
                                    </small>
                                </p>

                                <InputTextField
                                    v-model="internalClientRef"
                                    label="Purchase Order (Client Ref)"
                                    name="client_ref"
                                    placeholder="Client Ref"
                                    removeMargin
                                    required
                                />

                            </AdminBlock>
                        </div>

                        <div class="col-12 col-md-6">
                            <AdminBlock title="Mileage">
                                <div class="row">
                                    <div class="col-6">
                                        <InputTextField
                                            v-model="internalMileage"
                                            label="Estimated Mileage"
                                            name="mileage"
                                            placeholder="Mileage"
                                            append="Miles"
                                            readonly
                                        />
                                    </div>
                                    <div class="col-6">
                                        <InputTextField
                                            v-model="internalReported"
                                            label="Reported Mileage"
                                            name="reported_mileage"
                                            placeholder="Mileage"
                                            append="Miles"
                                            readonly
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-12">
                                        <InputTextField
                                            type="number"
                                            v-model="internalInvoicedMileage"
                                            label="Mileage to Invoice (if different to Estimated)"
                                            name="invoiced_mileage"
                                            :placeholder="'Internal Mileage is ' + internalMileage.toString()"
                                            append="Miles"
                                            removeMargin
                                        />
                                    </div>
                                </div>
                            </AdminBlock>
                        </div>

                        <div class="col-12 col-md-6">
                            <AdminBlock title="Contract">

                                <ContractSelect
                                    v-model="selectedContract"
                                    :contract="job.contract"
                                    :customer="job.customer"
                                    :errors="errors"
                                />

                                <a :href="generateMoveToDifferentCustomerRoute()" class="btn btn-sm btn-secondary">
                                    Move to a different Customer
                                </a>

                            </AdminBlock>
                        </div>

                        <div class="col-12 col-md-6">
                            <AdminBlock title="Vehicle">
                                <VehicleSelect
                                    v-model="selectedVehicle"
                                    :vehicle="selectedVehicle"
                                    :customer="job.customer"
                                    :errors="errors"
                                    removeMargin
                                    no-label
                                />
                            </AdminBlock>
                        </div>
                    </div>
                </div>

                <div class="col-12 col-md-6 col-lg-3">
                    <AdminBlock title="Price">

                        <RoutePrice
                            :contract_id="selectedContract?.id"
                            :vehicle_id="selectedVehicle?.id"
                            :mileage="computedInvoicedMileage"
                            :price="internalPrice"
                            :fuel="job.price_includes_fuel"
                            :adblue="job.price_includes_adblue"
                            @update-price="updatePrice"
                            @update-price-only="updatePriceOnly"
                            @update-adblue="updateAdblue"
                            @update-fuel="updateFuel"
                        />

                    </AdminBlock>
                </div>

            </div>

            <div class="row" v-if="job.status === 'aborted'">
                <div class="col-lg-12">
                    <AdminBlock title="Aborted">
                        <p>This Job was aborted.</p>
                        <div class="row">
                            <div class="col-lg-6">
                                <label>
                                    <strong>
                                        Driver Reason
                                    </strong>
                                </label>
                                <p>
                                    <em v-if="job.aborted_driver_reason">
                                        {{ job.aborted_driver_reason }}
                                    </em>
                                    <em v-else>
                                        No Driver reason given
                                    </em>
                                </p>
                            </div>
                            <div class="col-lg-6">
                                <label for="aborted_admin_reason">
                                    <strong>
                                        Admin Reason
                                    </strong>
                                </label>
                                <textarea name="aborted_admin_reason" id="" class="form-control w-100" rows="4" placeholder="Admin Reason" >{{ job.aborted_admin_reason }}</textarea>
                            </div>
                        </div>
                    </AdminBlock>
                </div>
            </div>

            <AdminBlock>
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-end align-items-center">
                            <div class="">
                                <button
                                    type="button"
                                    class="btn btn-success me-2"
                                    @click="sendFormRequest"
                                >
                                    Save
                                </button>
                                <a :href="generateSignOffExpensesRoute()" class="btn btn-secondary me-2">
                                    Proceed without saving
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </AdminBlock>

        </form>
    </div>

</template>

<script>

    import axios from "axios";
    import Swal from "sweetalert2";
    import AdminBlock from '@/components/movements/components/AdminBlock.vue';
    import ContractSelect from "@/components/movements/components/ContractSelect.vue";
    import InputTextField from "@/components/movements/components/InputTextField.vue";
    import InputNumberField from "@/components/movements/components/InputNumberField.vue";
    import RoutePrice from "@/components/movements/components/RoutePrice.vue";
    import VehicleSelect from "@/components/movements/components/VehicleSelect.vue";
    import {successAlert} from "@//utils";

    export default {

        components: {
            ContractSelect,
            VehicleSelect,
            InputNumberField,
            RoutePrice,
            InputTextField,
            AdminBlock,
        },

        props: {
            job: {
                type: Object,
                required: true,
            },
            reported: {
                type: Number,
                required: true,
            },
            errors: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                internalReported: this.reported ?? 0,
                selectedContract: this.job.contract.id ?? null,
                selectedVehicle: this.job.vehicle ?? null, // The Vehicle Select accepts a Vehicle Object

                internalMileage: this.job.mileage ?? 0,
                internalInvoicedMileage: this.job.invoiced_mileage ? Number(this.job.invoiced_mileage) : null,

                internalPrice: this.job.price ?? 0,
                internalPriceIncludesAdblue: this.job.price_includes_adblue ?? false,
                internalPriceIncludesFuel: this.job.price_includes_fuel ?? false,

                rateVehicletypeId: null,

                rateMinimumPrice: null,
                rateMinimumPriceMileage: null,
                ratePricePerMileAfterMin: null,

                price: this.job.price ?? 0,
                priceIncludesFuel: false,
                priceIncludesAdblue: false,

                internalClientRef: this.job.client_ref ?? '',

            }
        },


        methods: {
            async sendFormRequest() {
                // Check for TBC warning
                if (this.internalClientRef && this.internalClientRef.trim().toUpperCase() === 'TBC') {
                    const result = await Swal.fire({
                        title: 'Warning: TBC Purchase Order',
                        html: '<p>The PO Reference is set to "TBC".</p><p>This will need to be updated with a valid PO reference before invoicing.</p>',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Continue Anyway',
                        cancelButtonText: 'Go Back',
                        confirmButtonColor: '#ffc107',
                        cancelButtonColor: '#6c757d',
                    });

                    if (!result.isConfirmed) {
                        return;
                    }
                }

                const data = new FormData();

                data.append('job_id', this.job.id);

                if (this.job.status === "aborted") {
                    data.append('aborted_admin_reason', this.job.aborted_admin_reason);
                }

                data.append('client_ref', this.internalClientRef);

                // Internal Mileage is mileage on the Job
                // InternalInvoicedMileage is input as an override

                if ( this.internalInvoicedMileage && this.internalInvoicedMileage !== this.internalMileage ) {
                    data.append('invoiced_mileage', this.internalInvoicedMileage);
                } else {
                    data.append('invoiced_mileage', this.internalMileage);
                }

                data.append('contract_id', this.selectedContract?.id);
                data.append('vehicle_id', this.selectedVehicle?.id);

                // This is because in the API I'm returning it using NumberFormat. I need to format it on the frontend
                data.append('price', parseFloat(this.price.replace(/,/g, '')));
                data.append('price_includes_adblue', this.priceIncludesAdblue);
                data.append('price_includes_fuel', this.priceIncludesFuel);

                axios.post(route('api.movements.jobs.signoff.process', {job: this.job.id}), data)
                    .then(() => {
                        successAlert('The job Ready to Invoice stage one updated successfully', 'Job Stage One co,')
                        .then( () => {
                            window.location.href = route('movements.jobs.signoff.expenses', {job: this.job.id});
                        })
                    })
                    .catch(error => {

                        console.error(error);

                        if ( !error.response ) {
                            return;
                        }

                        let allErrors = '';

                        if ( error.response?.data?.errors?.length > 0 ) {

                            const errorMessages = error.response.data.errors;

                            Object.entries(errorMessages).forEach(([, messages]) => {
                                messages.forEach(errorMessage => {
                                    allErrors += '<p class="mb-0">' + errorMessage + '</p>';
                                });
                            });

                        } else if ( error.response?.data?.message ) {
                            allErrors = error.response.data.message;
                        }

                        Swal.fire({
                            toast: true,
                            position: 'top-end',
                            icon: 'error',
                            title: 'Error Generating Price',
                            html: allErrors,
                            width: '600px',
                            confirmButtonText: 'Dismiss',
                            confirmButtonColor: '#dc3545 ',
                            didOpen: (toast) => {
                                toast.addEventListener('mouseenter', Swal.stopTimer);
                                toast.addEventListener('mouseleave', Swal.resumeTimer);
                            },
                        });
                    });
            },

            updateSelectedContract(value) {
                this.selectedContract = value;
            },

            generateMoveToDifferentCustomerRoute() {
                return route('movements.jobs.move.index', {job: this.job.id});
            },

            generateSignOffExpensesRoute() {
                return route('movements.jobs.signoff.expenses', {job: this.job.id});
            },

            updatePrice(values) {
                this.price = values.price;
                this.rateMinimumPrice = values.rate.minimum_price;
                this.rateMinimumPriceMileage = values.rate.minimum_price_mileage;
                this.ratePricePerMileAfterMin = values.rate.price_per_mile_after_min;
                this.rateVehicletypeId = values.rate.vehicletype_id;
            },

            updatePriceOnly(newPrice) {
                this.price = newPrice;
            },

            updateAdblue( value ) {
                this.priceIncludesAdblue = value;
            },

            updateFuel( value ) {
                this.priceIncludesFuel = value;
            },


        },

        computed: {
            /**
             * Matches the server's outstanding-PO predicate, TBC and TBA
             * included (D6) — the same fact the confirmation step's notice is
             * about, so the two steps cannot disagree about whether this job
             * has a purchase order.
             */
            poIsOutstanding() {
                if (this.job.customer.requires_po_for_invoice !== true) {
                    return false;
                }

                const reference = (this.internalClientRef ?? '').trim().toUpperCase();

                return reference.length === 0 || reference === 'TBC' || reference === 'TBA';
            },

            computedInvoicedMileage: {
                get() {
                    return this.internalInvoicedMileage || this.internalMileage;
                },
                set(value) {
                    this.internalInvoicedMileage = value;
                }
            },
        },

    }

</script>
